import { useEffect, useState } from "react";
import { getLocalizedName } from "common";
import { filtersApi } from "common/api/filters";
import { rolesApi } from "common/api/roles";
import {
  getFilteredDefaults,
  validateFilterFields,
} from "common/filter/form/functions";
import { FilterDefaults } from "common/filter/form/defaults";
import { FormValidationProps } from "common/form/types";
import { isFilterEditable } from "common/functions/filters";
import { hasPermissionToRead } from "common/functions/roles";
import { Context } from "common/types/context";
import type { Filter } from "common/types/filters";
import { Role } from "common/types/roles";
import { VerticalField } from "common/ui/field";
import { GeneralPermissionsError } from "common/widgets/error";
import { ToggleWithLabel } from "common/widgets/toggle/with-label";
import { StringInput } from "common/widgets/input-with-submit/string";
import { ValueProps } from "common/with-value-for";
import { withUnique } from "common/widgets/unique/base";
import { wrapWithUniqueResult } from "common/widgets/unique/functions";
import { Tab } from "common/widgets/tabs/tab";
import { Tabs } from "common/widgets/tabs/tabs";
import {
  InputWithSubmit,
  type PropTypes as InputWithSubmitPropTypes,
} from "common/widgets/input-with-submit";
import { getFiltersPermissions } from "x/records/list/functions";
import { FilterConfigurations } from "./configurations";

interface PropTypes extends ValueProps<Filter>, FormValidationProps {
  context: Context;
  className?: string;
  displayEntity?: boolean;
  disableDefaultsChange?: boolean;
  disableGranularShare?: boolean;
  filterDefaultsOnChange?: boolean;
  onSave?: () => void;
}

const UniqueViewName = withUnique<InputWithSubmitPropTypes>(InputWithSubmit);

export const FilterEditForm = (props: PropTypes) => {
  const {
    context,
    className,
    displayEntity,
    formValidation,
    onFormValidationChange,
    disableDefaultsChange,
    disableGranularShare,
    filterDefaultsOnChange = true,
    onSave,
    value,
    onChange,
  } = props;
  const { apiCall, entities, role, userTypes, site } = context;
  const { entity: entityName, name, shared, defaults } = value;

  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [areRolesLoading, setAreRolesLoading] = useState<boolean>(false);

  const entity = entities[entityName];
  const canAccessRoles = hasPermissionToRead(userTypes, role, "Roles");
  const { canCreateShared } = getFiltersPermissions(context, ["CreateShared"]);

  const validateFilter = (filter: Filter) => {
    const filterValidationForm = validateFilterFields(
      filter,
      context,
      formValidation,
    );
    onFormValidationChange?.(filterValidationForm);
  };

  useEffect(() => {
    validateFilter(value);

    if (!canAccessRoles) return;

    setAreRolesLoading(true);
    rolesApi(apiCall)
      .list()
      .then((roles) => {
        setAllRoles(roles);
      })
      .finally(() => setAreRolesLoading(false));
  }, []);

  const handleFieldChange = (newValue: Filter) => {
    validateFilter(newValue);
    onChange(newValue);
  };

  const onNameChange = (name: string) => {
    const { value, onChange } = props;
    onChange({ ...value, name });
  };

  const onSitesChange = (sites: string[]) => {
    const newValue = {
      ...value,
      sites,
      defaults: filterDefaultsOnChange
        ? getFilteredDefaults(value, sites)
        : defaults,
    };
    handleFieldChange(newValue);
  };

  const onRolesChange = (roleIds: number[]) => {
    const newValue = {
      ...value,
      roleIds,
      defaults: filterDefaultsOnChange
        ? getFilteredDefaults(value, undefined, roleIds)
        : defaults,
    };
    handleFieldChange(newValue);
  };

  const onSharedChange = (shared: boolean) => {
    const newValue: Filter = {
      ...value,
      shared,
      sites: shared ? [site.name] : [],
      roleIds: [],
      defaults: [],
    };
    handleFieldChange(newValue);
  };

  const checkUniqueName = (name: string) =>
    filtersApi(apiCall).isNameUnique(name, value?.id);

  const isEditable = isFilterEditable(context, value);
  if (!isEditable) return <GeneralPermissionsError />;

  return (
    <div className={className}>
      {displayEntity ? (
        <VerticalField
          label={_("Entity")}
          input={
            <StringInput
              disabled={true}
              value={getLocalizedName(entity) || entityName}
              onChange={undefined}
            />
          }
        />
      ) : undefined}
      <VerticalField
        label={_("View name")}
        error={!name}
        input={
          <UniqueViewName
            columName={"name"}
            columnLocalizedName={_("View name")}
            fetchIsUnique={wrapWithUniqueResult(checkUniqueName)}
            useUnique={true}
            formValidation={formValidation}
            onFormValidationChange={onFormValidationChange}
            onSubmit={onSave}
            value={name}
            onChange={onNameChange}
          />
        }
      />
      {!disableGranularShare && canCreateShared ? (
        <div>
          <label key="shared-input" className="qa-label-checkbox">
            <ToggleWithLabel
              label={_("Shared")}
              value={shared}
              onChange={onSharedChange}
            />
          </label>
          {shared &&
            (canAccessRoles ? (
              <Tabs key="view-tabs" className={"x-margin-top-10"}>
                <Tab value="configuration" label={_("Configuration")}>
                  <FilterConfigurations
                    context={context}
                    allRoles={allRoles}
                    value={value}
                    onSitesChange={onSitesChange}
                    onRolesChange={onRolesChange}
                    isLoading={areRolesLoading}
                  />
                </Tab>
                {!disableDefaultsChange ? (
                  <Tab value={"defaults"} label={_("Defaults")}>
                    <FilterDefaults
                      context={context}
                      allRoles={allRoles}
                      value={value}
                      onChange={onChange}
                    />
                  </Tab>
                ) : undefined}
              </Tabs>
            ) : (
              <FilterConfigurations
                context={context}
                allRoles={allRoles}
                value={value}
                onSitesChange={onSitesChange}
                onRolesChange={onRolesChange}
                isLoading={areRolesLoading}
              />
            ))}
        </div>
      ) : undefined}
    </div>
  );
};
